import React, { useEffect, useRef, useState } from 'react';
import { Globe2, LayoutGrid, Crosshair, Timer } from 'lucide-react';

const ICONS = [Globe2, LayoutGrid, Crosshair, Timer];
const SUBS = [
  'Curated, vertical-aware routing',
  'Direct-response → broad reach',
  'Whitelist · Blacklist · Placements',
  'From application to live spend',
];

function useCountUp(target, durationMs = 1400, start = false) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { setVal(target); return; }
    let raf; const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / durationMs);
      setVal(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => raf && cancelAnimationFrame(raf);
  }, [target, durationMs, start]);
  return val;
}

export default function StatsStrip({ items = [] }) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver((es) => {
      es.forEach((e) => { if (e.isIntersecting) { setSeen(true); io.disconnect(); } });
    }, { threshold: 0.35 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4" data-testid="stats-strip">
      {items.map((s, i) => (
        <Stat key={i} item={s} index={i} start={seen} />
      ))}
      <div className="md:col-span-4 mt-3 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[11.5px] text-slate-400">
        <Pill>Curated Sources</Pill>
        <Pill>Anti-Fraud Review</Pill>
        <Pill>Whitelist / Blacklist Support</Pill>
        <Pill>Managed Optimization</Pill>
      </div>
    </div>
  );
}

function Stat({ item, index, start }) {
  const v = useCountUp(item.value, 1400, start);
  const Icon = ICONS[index] || Globe2;
  return (
    <div
      data-testid="stats-strip-item"
      className="relative rounded-2xl bg-white/[0.03] border border-white/10 backdrop-blur-xl p-5 lift-on-hover overflow-hidden"
    >
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(34,211,238,0.55), rgba(124,58,237,0.55), transparent)' }}
        aria-hidden
      />
      <div className="flex items-start justify-between">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#2563EB]/20 via-[#22D3EE]/20 to-[#7C3AED]/20 border border-white/10">
          <Icon className="h-4 w-4 text-[#22D3EE]" />
        </span>
        <span className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500">live</span>
      </div>
      <div className="mt-4 font-display text-[34px] sm:text-[40px] font-bold tracking-[-0.03em] text-white font-mono-tabular leading-none">
        {v.toLocaleString()}<span className="text-[#22D3EE]">{item.suffix}</span>
      </div>
      <div className="mt-2 text-[12.5px] text-slate-300 font-medium">{item.label}</div>
      <div className="mt-1 text-[11.5px] text-slate-500">{SUBS[index] || ''}</div>
    </div>
  );
}

function Pill({ children }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/[0.03] border border-white/10">
      <span className="h-1.5 w-1.5 rounded-full bg-[#22D3EE]" /> {children}
    </span>
  );
}
